import { Box, Button, Checkbox, DesktopModal, Empty, Flex, IconFont, Input, Modal, Tag, Text, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { ApplicationType, Order } from '@lighthouse/core'
import type { StatusFile } from '@lighthouse/shared'
import { getBrandInfo, timeDistance, TooltipText, useAtomAction, useAtomData } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetState, useWindowSize } from 'react-use'
import { useImmer } from 'use-immer'

import { copyApplicationAtom, createApplicationAtom, fetchApplicationListAtom, removeApplicationAtom } from '@/atoms/application/action'
import { appListAtom } from '@/atoms/application/state'
// import type { AppCreatorState } from '@/components/AppCreator'
import { AppCreator } from '@/components/AppCreator'
import { ImportLoading } from '@/components/ImportLoading'
import { ModalTitle } from '@/components/ModalTitle'
import { useCurrentSpaceID } from '@/hooks/useWorkSpace'
import type { CreateApplicationPayload } from '@/services/types'

import { blankTemplateWxAppletId } from '../Template/constant'
import TemplateModal from '../Template/TemplateModal'
import type { ApplicationFilterAndSortParams } from './ApplicationToolBar'
import { ApplicationToolBar } from './ApplicationToolBar'
import { AppFilterType } from './ApplicationToolBar/AppFilter'
import { AppSortType } from './ApplicationToolBar/AppSort'
import { HandleSelect } from './HandleSelect'
import { PromotionNotice } from './PromotionNotice'
import * as SC from './styles'

// const guideStepImg7 = getAssetUrl('guide', 'guide_step7.jpg')
const HelpBuild = getAssetUrl('application', 'HelpBuild.png')
const Cart = getAssetUrl('application', 'Cart.png')
const Chart = getAssetUrl('application', 'Chart.png')
const LeftBlank = getAssetUrl('application', 'LeftBlank.png')
const Mouse = getAssetUrl('application', 'Mouse.png')
const PaperPlane = getAssetUrl('application', 'PaperPlane.png')
const ResolveCase = getAssetUrl('application', 'ResolveCase.png')
const ResolveCaseBackground = getAssetUrl('application', 'ResolveCaseBackground.png')
const ResolveCaseTiny = getAssetUrl('application', 'ResolveCaseTiny.png')
const RightBlank = getAssetUrl('application', 'RightBlank.png')
const TemplateDesktopWeb = getAssetUrl('application', 'TemplateDesktopWeb.png')
const TemplateMobileWeb = getAssetUrl('application', 'TemplateMobileWeb.png')
const Users = getAssetUrl('application', 'Users.png')

const emptyApplicationImg = getAssetUrl('empty', 'empty_application.png')

export const ApplicationList: React.FC = () => {
    const listRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()
    const { width } = useWindowSize()
    const spaceId = useCurrentSpaceID()
    // const appList = useApplicationList()
    const [params, setParams] = useImmer<ApplicationFilterAndSortParams>({
        search: '',
        filter: AppFilterType.ALL,
        sort: AppSortType.CREATE_TIME,
        order: Order.DESC
    })
    const { search, filter, sort, order } = params
    const [{ appName, fileStatus, open, isBlankTemplate, openLoading, openTemplate, createAppType }, setState] = useSetState<{
        appName: string
        fileStatus: StatusFile | null
        open: boolean
        isBlankTemplate: boolean
        openLoading: boolean
        openTemplate: boolean
        createAppType: ApplicationType
    }>({
        appName: '',
        fileStatus: null,
        open: false,
        isBlankTemplate: false,
        openLoading: false,
        openTemplate: false,
        createAppType: ApplicationType.WEBSITE
    })
    const { run: createApplication } = useAtomAction(createApplicationAtom)
    const { run: removeApplication } = useAtomAction(removeApplicationAtom)
    const { run: copyApplication } = useAtomAction(copyApplicationAtom)
    const appList = useAtomData(appListAtom)
    const { run: fetchApplicationList } = useAtomAction(fetchApplicationListAtom)

    // const { loading, value: appList = [], retry } = useAsyncRetry(() => fetchApplicationList(spaceId), [spaceId])

    const applicationList = useMemo(() => {
        const searchVal = search.trim()
        const list = appList.filter(app => {
            const isSearch = !searchVal || (searchVal && app.name.includes(searchVal))
            const isFilter =
                !filter ||
                filter === AppFilterType.ALL ||
                (filter === AppFilterType.PUBLIC && app.isPublish) ||
                (filter === AppFilterType.NO_PUBLIC && !app.isPublish) ||
                (filter === AppFilterType.PUBLIC_PRIVATE_TEMPLATE && app.hasPrivateTemplate) ||
                (filter === AppFilterType.PUBLIC_MINI_PROGRAM && app.authMiniProgram)
            return isSearch && isFilter
        })

        switch (sort) {
            case AppSortType.RECENTLY: {
                return list.sort((a, b) => {
                    return order === Order.ASC ? a.updatedTime - b.updatedTime : b.updatedTime - a.updatedTime
                })
            }
            case AppSortType.CREATE_TIME: {
                return list.sort((a, b) => {
                    return order === Order.ASC ? a.createdTime - b.createdTime : b.createdTime - a.createdTime
                })
            }
            default: {
                return list.sort((a, b) => {
                    if (a.name > b.name) {
                        return order === Order.ASC ? 1 : -1
                    }
                    if (a.name < b.name) {
                        return order === Order.ASC ? -1 : 1
                    }
                    return 0
                })
            }
        }
    }, [appList, filter, order, search, sort])

    // 删除确认Modal状态管理
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
    const [deleteConfirmName, setDeleteConfirmName] = useState('')
    const [riskConfirmed, setRiskConfirmed] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)

    // 打开删除确认弹窗
    const showDeleteModal = useCallback((id: string, name: string) => {
        setDeleteTarget({ id, name })
        setDeleteConfirmName('')
        setRiskConfirmed(false)
        setDeleteLoading(false)
        setDeleteModalOpen(true)
    }, [])

    // 关闭删除确认弹窗
    const closeDeleteModal = useCallback(() => {
        setDeleteModalOpen(false)
        setDeleteTarget(null)
        setDeleteConfirmName('')
        setRiskConfirmed(false)
        setDeleteLoading(false)
    }, [])

    // 执行删除操作
    const confirmDelete = useCallback(async () => {
        if (!deleteTarget) {
            return
        }

        setDeleteLoading(true)
        try {
            const isDelete = await removeApplication(deleteTarget.id)
            if (isDelete) {
                Toast.success('应用删除成功')
                fetchApplicationList(spaceId)
            }
            closeDeleteModal()
        } catch {
            Toast.error('应用删除失败')
            fetchApplicationList(spaceId)
        } finally {
            setDeleteLoading(false)
        }
    }, [deleteTarget, removeApplication, spaceId, fetchApplicationList, closeDeleteModal])

    // 检查是否可以删除
    const canDelete = riskConfirmed && deleteConfirmName === deleteTarget?.name

    const handleRemove = useCallback(
        (id: string, name: string) => {
            showDeleteModal(id, name)
        },
        [showDeleteModal]
    )

    const handleCopy = useCallback(
        async (id: string, name: string) => {
            const isConfirm = await Modal.confirm({
                title: '确认复制',
                content: `复制应用「${name}」？`,
                okStatus: 'primary',
                okText: '确认'
            })

            if (isConfirm) {
                try {
                    setState({ openLoading: true, appName: name })
                    const copyId = await copyApplication(id)
                    setState({ openLoading: false, appName: '' })
                    if (copyId) {
                        Toast.success('应用复制成功')
                        fetchApplicationList(spaceId)
                    }
                } catch {
                    Toast.error('复制失败')
                    fetchApplicationList(spaceId)
                }
            }
        },
        [copyApplication, fetchApplicationList, setState, spaceId]
    )

    const handlers = useMemo(
        () => ({
            handleSelectTemplate: (templateId: string) => {
                setState({
                    openTemplate: true,
                    open: true,
                    createAppType: templateId === blankTemplateWxAppletId ? ApplicationType.WX_APPLET : ApplicationType.WEBSITE
                    // createAppType: ApplicationType.website,
                    // createWay: templateId === 'ai' ? 'ai' : 'common'
                })
            }
        }),
        [setState]
    )

    const handleOpenHelpBuild = useCallback(() => {
        window.open(getBrandInfo().space.helpBuildUrl, '_blank')
    }, [])

    const handleOpenResolved = useCallback(() => {
        window.open(getBrandInfo().space.resolvedUrl, '_blank')
    }, [])

    const handleOpenCreateApp = useCallback(() => {
        setState({ openTemplate: true, isBlankTemplate: true })
    }, [setState])

    const handleOpenTemplate = useCallback(() => {
        setState({ openTemplate: true, isBlankTemplate: false })
    }, [setState])

    const handleCreateApp = useCallback(
        (params: Omit<CreateApplicationPayload, 'spaceId'>) => {
            return createApplication({ spaceId, ...params })
        },
        [createApplication, spaceId]
    )

    // const handleCreateAppByAI = useCallback(
    //     async (prompt: string) => {
    //         try {
    //             const appId = await createAppByAIPrompt({ spaceId, prompt, icon: getRandomIcon() })
    //             setState({ open: false })
    //             if (appId) {
    //                 navigate({ pathname: `/${appId}` })
    //             }
    //         } catch {
    //             Toast.error('创建失败，请重试')
    //             setState({ open: false })
    //         }
    //     },
    //     [navigate, setState, spaceId]
    // )

    const handleLinkToApplication = useCallback(
        (appId: string) => {
            navigate({ pathname: `/${appId}/page` })
        },
        [navigate]
    )

    const handleLinkToHelp = useCallback(() => {
        window.open('https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich?#', '_blank')
    }, [])

    useEffect(() => {
        const listElement = listRef.current
        if (listElement) {
            const { width } = listElement.getBoundingClientRect()
            const count = Math.floor(width / 224) - 1
            listElement.style.gridTemplateColumns = `repeat(${count}, 1fr)`
        }
    }, [width])

    return (
        <SC.Container>
            <SC.Header>
                {/* <SC.Title size={16}>你可能想尝试...</SC.Title> */}
                <PromotionNotice />
            </SC.Header>
            <SC.Utils>
                <Flex style={{ flex: 1 }} gap={16}>
                    <SC.TemplateItem onClick={handleOpenTemplate}>
                        <SC.UtilsHead>
                            <SC.UtilsTitle>模板库</SC.UtilsTitle>
                            {/* <SC.Arrow type="ArrowCircle" /> */}
                        </SC.UtilsHead>
                        <SC.UtilsContent>
                            <SC.TemplateDesktopWebImg src={TemplateDesktopWeb} alt="TemplateWeb" />
                            <SC.TemplateMobileWebImg src={TemplateMobileWeb} alt="TemplateWeb" />
                            <SC.LeftBlankImg src={LeftBlank} alt="TemplateWeb" />
                            <SC.RightBlankImg src={RightBlank} alt="TemplateWeb" />

                            <SC.CartImg src={Cart} alt="Cart" />
                            <SC.ChartImg src={Chart} alt="Chart" />
                            <SC.PaperPlaneImg src={PaperPlane} alt="PaperPlane" />
                            <SC.UsersImg src={Users} alt="Users" />
                        </SC.UtilsContent>
                    </SC.TemplateItem>
                    <Flex style={{ flex: 1 }} gap={16}>
                        <SC.AiItem onClick={handleOpenHelpBuild}>
                            <SC.UtilsHead>
                                <SC.UtilsTitle>帮你搭建</SC.UtilsTitle>
                                {/* <SC.Arrow type="ArrowCircle" /> */}
                            </SC.UtilsHead>
                            <SC.UtilsContent>
                                <SC.BuildImg src={HelpBuild} alt="HelpBuild" />
                                <SC.MouseImg src={Mouse} alt="Mouse" />
                            </SC.UtilsContent>
                            <SC.PurpleBackground style={{ position: 'absolute', top: '61px', left: '-180px' }} />
                            <SC.CyanBackground style={{ position: 'absolute', top: '-85px', right: '-102px' }} />
                        </SC.AiItem>
                        <SC.ResolvedItem
                            onClick={handleOpenResolved}
                            // onMouseEnter={() => handleSetGif('helpBuildImg', HelpBuildGif)}
                            // onMouseLeave={() => {
                            //     handleSetGif('helpBuildImg', HelpBuild)
                            // }}
                        >
                            <SC.UtilsHead>
                                <SC.UtilsTitle>解决方案</SC.UtilsTitle>
                                {/* <SC.Arrow type="ArrowCircle" /> */}
                            </SC.UtilsHead>
                            <SC.UtilsContent>
                                <SC.ResolveCaseImg src={ResolveCase} alt="AiInput" />
                                <SC.ResolveCaseTinyImg src={ResolveCaseTiny} alt="AiInput" />
                                <SC.ResolveCaseBackgroundImg src={ResolveCaseBackground} alt="AiInput" />
                            </SC.UtilsContent>
                            <SC.CyanBackground style={{ position: 'absolute', top: '-85px', left: '-98px' }} />
                            <SC.PurpleBackground style={{ position: 'absolute', top: '8px', right: '-112px' }} />
                        </SC.ResolvedItem>
                    </Flex>
                </Flex>
                <SC.HelpItem width={142} onClick={handleLinkToHelp}>
                    <SC.UtilsHead>
                        <SC.UtilsTitle>帮助中心</SC.UtilsTitle>
                        <SC.Arrow type="ArrowCircle" />
                    </SC.UtilsHead>
                    <SC.UtilsContent>
                        <SC.HelpImg src={getAssetUrl('common', 'Help.svg')} alt="help" />
                    </SC.UtilsContent>
                </SC.HelpItem>
            </SC.Utils>
            <ModalTitle title="所有应用" style={{ marginTop: 20 }} extra={<ApplicationToolBar data={params} setData={setParams} />} />
            {applicationList.length === 0 && search && (
                <Empty
                    styles={{
                        root: {
                            width: '100%'
                        },
                        wrapper: {
                            gap: 20
                        },
                        content: {
                            lineHeight: '22px'
                        }
                    }}
                    description={`找不到符合“${search}”的应用`}
                    icon={<img width={140} src={getAssetUrl('empty', 'empty_applications.svg')} alt="" />}
                />
            )}
            <SC.Applications ref={listRef}>
                {!search && (
                    <SC.ApplicationCard style={{ paddingTop: 64 }} onClick={handleOpenCreateApp}>
                        <SC.ApplicationCardImage>
                            <SC.ApplicationCardLogo background="var(--color-gray-900)" radius="50%">
                                <IconFont fill="var(--color-white)" type="Add" size={24} />
                            </SC.ApplicationCardLogo>
                            <SC.ApplicationCardTitle>创建应用</SC.ApplicationCardTitle>
                        </SC.ApplicationCardImage>
                    </SC.ApplicationCard>
                )}

                {applicationList.map(item => (
                    <SC.ApplicationCard key={item.id} onClick={() => handleLinkToApplication(item.id)}>
                        <SC.CardHeader justifyContent="space-between" direction="row-reverse">
                            <HandleSelect id={item.id} name={item.name} onRemove={handleRemove} onCopy={handleCopy} />
                            <Flex alignItems="center" gap={8}>
                                {item.hasPrivateTemplate && (
                                    <Tag radius={6} background="var(--color-gray-100)">
                                        <Flex gap={4} alignItems="center">
                                            <IconFont fill="var(--color-gray-400)" type="HandShake" />
                                            <Text>私人模板</Text>
                                        </Flex>
                                    </Tag>
                                )}
                                {item.authMiniProgram && (
                                    <Tag radius={6} background="var(--color-green-100)">
                                        <Flex gap={4} alignItems="center">
                                            <IconFont fill="var(--color-green-500)" type="MiniProgram" />
                                            <Text color="var(--color-green-500)">小程序</Text>
                                        </Flex>
                                    </Tag>
                                )}
                            </Flex>
                        </SC.CardHeader>
                        <SC.ApplicationCardImage>
                            <SC.ApplicationCardLogo background={item.primaryColor}>
                                <IconFont fill="var(--color-white)" type={item.icon} size={24} />
                            </SC.ApplicationCardLogo>
                            <SC.ApplicationCardTitle>
                                <TooltipText
                                    title={item.name}
                                    render={ref => {
                                        return (
                                            <SC.ApplicationCardText ref={ref} lineClamp={1} whiteSpace="nowrap">
                                                {item.name}
                                            </SC.ApplicationCardText>
                                        )
                                    }}
                                />
                            </SC.ApplicationCardTitle>
                            <SC.ApplicationCardDescribe justifyContent="center" alignItems="center" gap={8}>
                                {item.isPublish && (
                                    <>
                                        <Flex gap={6} alignItems="center">
                                            <SC.Round />
                                            <Text lineClamp={1} color="var(--color-green-500)" size={12} weight={400}>
                                                已发布
                                            </Text>
                                        </Flex>
                                        <SC.Vertical />
                                    </>
                                )}
                                <Text lineClamp={1} color="var(--color-gray-400)" size={12} weight={400}>
                                    {timeDistance(item.updatedTime)}
                                </Text>
                            </SC.ApplicationCardDescribe>
                        </SC.ApplicationCardImage>
                    </SC.ApplicationCard>
                ))}
            </SC.Applications>
            <TemplateModal
                opened={openTemplate}
                isBlankTemplate={isBlankTemplate}
                onClose={() => {
                    setState({ openTemplate: false })
                }}
                onCreateBlankApp={handlers.handleSelectTemplate}
            />

            <ImportLoading
                open={openLoading}
                title={`正在复制应用“${appName}”`}
                describe="请不要刷新页面，避免复制出错"
                hidePercent
                fileStatus={fileStatus}
                onClose={() => setState({ openLoading: false })}
            />

            <AppCreator
                open={open}
                // open
                appType={createAppType}
                onClose={() => {
                    setState({ open: false })
                }}
                onConfirm={handleCreateApp}
            />

            {/* <AppAICreator
                opened={createWay === 'ai' && open}
                onClose={() => {
                    setState({ open: false })
                }}
                onConfirm={value => {
                    handleCreateAppByAI(value)
                }}
            /> */}

            {/* 删除确认Modal */}
            <DesktopModal
                open={deleteModalOpen}
                width={454}
                withCloseIcon={false}
                closeOnClickOverlay={false}
                onClose={closeDeleteModal}
                styles={{
                    body: {
                        padding: '24px !important'
                    }
                }}
                footer={
                    <Flex px={24} pt={8} pb={24} justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <Checkbox
                            checked={riskConfirmed}
                            size="xs"
                            label="我已知晓删除应用的风险"
                            onChange={e => setRiskConfirmed(e.target.checked)}
                            style={{ color: 'var(--color-gray-500)' }}
                        />
                        <Flex gap={8}>
                            <Button size="lg" onClick={closeDeleteModal} disabled={deleteLoading}>
                                取消
                            </Button>
                            <Button
                                size="lg"
                                type="primary"
                                danger
                                disabled={!canDelete || deleteLoading}
                                loading={deleteLoading}
                                onClick={confirmDelete}
                            >
                                {deleteLoading ? '删除中...' : '删除'}
                            </Button>
                        </Flex>
                    </Flex>
                }
            >
                <Text size={16} weight={600} color="var(--color-gray-900)" mb={12}>
                    确认删除应用？
                </Text>
                <Flex direction="column" gap={12}>
                    <SC.DeleteAppTips>删除后应用不可撤销，应用将无法访问，与应用相关的功能将不可用。请谨慎操作！</SC.DeleteAppTips>
                    <Text color="var(--color-gray-900)" size={14}>
                        输入应用的名称以确认删除应用：
                    </Text>
                    <Input
                        size="lg"
                        placeholder="请输入应用名称"
                        value={deleteConfirmName}
                        onChange={e => setDeleteConfirmName(e.target.value)}
                        disabled={deleteLoading}
                    />
                </Flex>
            </DesktopModal>
        </SC.Container>
    )
}
